import React, { useState, useMemo, useEffect } from 'react';
import {
  Card, Typography, Button, Table, Tag, Space, Modal, Form,
  Input, Select, Row, Col, message, Divider, Grid, List, DatePicker,
  Tooltip, Radio, ConfigProvider
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, FilterOutlined, ShopOutlined,
  FileTextOutlined, SearchOutlined, CalendarOutlined, EditOutlined, WalletOutlined, DollarOutlined, BankOutlined, WarningOutlined
} from '@ant-design/icons';
import { useCaja } from '../../hooks/useCaja';
import { useDeleteCompra, useGetCompras, useSaveCompra } from '../../hooks/useCompras';
import { useGetProductos } from '../../hooks/useProductos';
import { useGetProveedores } from '../../hooks/useProveedores';
import LayoutHome from '../../components/layout/LayoutHome';
import dayjs from 'dayjs';
import es_ES from 'antd/locale/es_ES';
import { usePermission } from '../../hooks/usePermission';
import ModalNuevaCompra from './modals/ModalNuevaCompra';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const Compras = () => {

  const { hasPermission } = usePermission();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const { getCajaActiva } = useCaja();
  const cajaActiva = getCajaActiva?.data;

  const { data: comprasDB = [], isLoading: cargandoCompras } = useGetCompras();
  const { data: productosDB = [] } = useGetProductos();
  const { data: proveedoresDB = [] } = useGetProveedores();
  const saveCompra = useSaveCompra();
  const deleteCompra = useDeleteCompra();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Estados de Filtros
  const [busqueda, setBusqueda] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState('todos');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState(dayjs().startOf('month'));
  const [fechaHasta, setFechaHasta] = useState(dayjs().endOf('day'));
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  useEffect(() => {
    if (screens.md !== undefined) {
      setMostrarFiltros(screens.md);
    }
  }, [screens.md]);

  // Estados de Compra
  const [itemsCompra, setItemsCompra] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidadItem, setCantidadItem] = useState(1);
  const [costoItem, setCostoItem] = useState(0);

  const [isDetalleOpen, setIsDetalleOpen] = useState(false);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);
  const [editingCompra, setEditingCompra] = useState(null);

  const aplicarFiltroRapido = (e) => {
    const tipo = e.target.value;
    const hoy = dayjs();
    if (tipo === 'hoy') {
      setFechaDesde(hoy.startOf('day'));
      setFechaHasta(hoy.endOf('day'));
    } else if (tipo === 'semana') {
      setFechaDesde(hoy.startOf('week'));
      setFechaHasta(hoy.endOf('day'));
    } else if (tipo === 'mes') {
      setFechaDesde(hoy.startOf('month'));
      setFechaHasta(hoy.endOf('day'));
    }
  };

  const comprasFiltradas = useMemo(() => {
    return comprasDB.filter(c => {
      const proveedorNombre = c.proveedor_nombre || '';
      const factura = c.numero_factura || c.id || '';

      const cumpleBusqueda = proveedorNombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        factura.toLowerCase().includes(busqueda.toLowerCase());

      const cumpleProveedor = proveedorFiltro === 'todos' || c.proveedor_id === proveedorFiltro;
      const cumpleEstado = estadoFiltro === 'todos' || c.estado_pago === estadoFiltro;

      const fechaC = dayjs(c.fecha);
      const cumpleFecha = fechaC.isAfter(fechaDesde.startOf('day')) && fechaC.isBefore(fechaHasta.endOf('day'));

      return cumpleBusqueda && cumpleFecha && cumpleProveedor && cumpleEstado;
    });
  }, [comprasDB, busqueda, fechaDesde, fechaHasta, proveedorFiltro, estadoFiltro]);

  const statsPeriodo = useMemo(() => {
    const cantidad = comprasFiltradas.length;
    const totalInvertido = comprasFiltradas.reduce((sum, c) => sum + Number(c.total || 0), 0);
    const totalPorPagar = comprasFiltradas.filter(c => c.estado_pago === 'Por Pagar').reduce((sum, c) => sum + Number(c.total || 0), 0);
    return { cantidad, totalInvertido, totalPorPagar };
  }, [comprasFiltradas]);

  const totalCompraActual = itemsCompra.reduce((sum, item) => sum + item.subtotal, 0);

  const eliminarItem = (id) => setItemsCompra(itemsCompra.filter(item => item.producto_id !== id));

  const handleEditCompra = (compra) => {
    setEditingCompra(compra);
    const itemsFormateados = (compra.items || []).map(i => ({ ...i, key: i.producto_id }));
    setItemsCompra(itemsFormateados);
    form.setFieldsValue({
      proveedor: compra.proveedor_id,
      factura: compra.numero_factura,
      metodo_pago: compra.metodo_pago
    });
    setIsModalOpen(true);
  };

  const handleEliminarCompra = (compra) => {
    Modal.confirm({
      title: <span style={{ color: '#ff4d4f' }}>¿Eliminar Factura de Compra?</span>,
      content: 'Al eliminarla, se restará el stock de los productos, se revertirán los movimientos de caja y las deudas con el proveedor. Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar permanentemente',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true, style: { borderRadius: '8px' } },
      cancelButtonProps: { style: { borderRadius: '8px' } },
      centered: true,
      onOk: () => {
        deleteCompra.mutate(compra.id, {
          onSuccess: () => message.success("Factura eliminada. Inventario y finanzas revertidas."),
          onError: (error) => message.error(error.message || "Error al eliminar la compra.")
        });
      }
    });
  };

  const renderMetodoPago = (metodo) => {
    let icon, color;
    if (metodo === 'Efectivo Caja') { icon = <WalletOutlined />; color = '#faad14'; }
    else if (metodo === 'Efectivo Externo') { icon = <DollarOutlined />; color = '#52c41a'; }
    else if (metodo === 'Crédito') { icon = <ShopOutlined />; color = '#ef4444'; }
    else { icon = <BankOutlined />; color = '#22BABB'; }
    return <Space>{React.cloneElement(icon, { style: { color } })}<Text size="small">{metodo}</Text></Space>;
  };

  const columnasHistorial = [
    { title: 'FECHA', dataIndex: 'fecha', render: (t) => <Text type="secondary">{dayjs(t).format('DD/MM/YYYY')}</Text> },
    { title: 'FACTURA', dataIndex: 'numero_factura', render: (t) => <Text strong style={{ fontFamily: 'monospace' }}>{t || 'S/N'}</Text> },
    { title: 'PROVEEDOR', dataIndex: 'proveedor_nombre', render: (t) => <Text strong style={{ color: '#1e293b' }}>{t?.toUpperCase()}</Text> },
    { title: 'DESTINO', dataIndex: 'bodega_nombre', render: (t) => <Text style={{ color: '#0ea5e9', fontWeight: '600' }}><ShopOutlined /> {t || 'Bodega Principal'}</Text> },
    { title: 'MÉTODO', dataIndex: 'metodo_pago', render: (m) => renderMetodoPago(m) },
    { title: 'ESTADO', dataIndex: 'estado_pago', render: (e) => <Tag color={e === 'Por Pagar' ? 'error' : 'success'} style={{ fontWeight: 'bold', borderRadius: '6px' }}>{e?.toUpperCase()}</Tag> },
    { title: 'TOTAL', dataIndex: 'total', align: 'left', render: (t) => <Text strong style={{ fontSize: '14px' }}>${Number(t || 0).toLocaleString('es-CO')}</Text> },
    {
      title: 'ACCIONES', align: 'center', width: 140, render: (_, record) => {
        const esEfectivoCajaCerrada = record.metodo_pago === 'Efectivo Caja' && record.sesion_caja_id !== cajaActiva?.id;

        return (
          <Space size="small">
            <Tooltip title="Detalle de la factura">
              <Button type="text" icon={<FileTextOutlined />} style={{ color: '#64748b' }} onClick={() => { setCompraSeleccionada(record); setIsDetalleOpen(true); }} />
            </Tooltip>

            {esEfectivoCajaCerrada ? (
              <Tooltip title="Bloqueado: Esta compra pertenece a una caja ya cerrada.">
                <Button type="text" disabled icon={<EditOutlined />} />
              </Tooltip>
            ) : (
              <Tooltip title="Editar Factura">
                <Button type="text" icon={<EditOutlined />} style={{ color: '#22BABB' }} onClick={() => handleEditCompra(record)} />
              </Tooltip>
            )}

            {/* NUEVO BOTÓN ELIMINAR BLINDADO */}
            {esEfectivoCajaCerrada ? (
              <Tooltip title="El sistema bloquea esta acción por tu propia seguridad financiera. Eliminar una compra de un turno anterior alteraría los reportes de caja que ya cerraste y aprobaste, generando descuadres de dinero en el pasado. Las cajas cerradas son intocables para proteger tu contabilidad.">
                <Button type="text" disabled danger icon={<DeleteOutlined />} />
              </Tooltip>
            ) : (
              <Tooltip title={hasPermission('compras:eliminar') ? "Eliminar Factura" : "Sin permisos para eliminar"}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!hasPermission('compras:eliminar')}
                  onClick={() => handleEliminarCompra(record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  const columnasItems = [
    { title: 'Producto', dataIndex: 'nombre', render: (t) => <Text strong>{t}</Text> },
    { title: 'Cant.', dataIndex: 'cantidad', align: 'center', width: 80 },
    { title: 'Costo Unit.', dataIndex: 'costo_unitario', align: 'right', width: 120, render: (t) => `$${Number(t || 0).toLocaleString('es-CO')}` },
    { title: 'Subtotal', dataIndex: 'subtotal', align: 'right', width: 130, render: (t) => <Text strong style={{ color: '#22BABB' }}>${Number(t || 0).toLocaleString('es-CO')}</Text> },
    { title: '', align: 'center', width: 50, render: (_, r) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => eliminarItem(r.producto_id)} /> }
  ];

  return (
    <ConfigProvider locale={es_ES}>
      <LayoutHome pageSeleccionada="compras">
        <style>{`
        .tabla-compras-compacta .ant-table-tbody > tr > td {
          padding: 6px 8px !important; 
          line-height: 1.2 !important;
        }
        .tabla-compras-compacta .ant-table-thead > tr > th {
          padding: 10px 8px !important;
        }

        /* --- NUEVAS REGLAS PARA PANTALLAS GRANDES --- */
        /* Hacemos que la tabla ocupe siempre el 100% del alto disponible */
        .tabla-compras-compacta {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .tabla-compras-compacta .ant-spin-nested-loading,
        .tabla-compras-compacta .ant-spin-container {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .tabla-compras-compacta .ant-table {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .tabla-compras-compacta .ant-table-container {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .tabla-compras-compacta .ant-table-body {
          flex: 1;
          overflow-y: auto !important;
        }
        /* Empuja la paginación siempre al final de la tarjeta */
        .tabla-compras-compacta .ant-pagination {
          margin-top: auto !important;
          margin-bottom: 0 !important;
          padding: 12px 16px;
          border-top: 1px solid #f1f5f9;
        }
      `}</style>

        <div style={{ padding: isMobile ? '8px' : '16px', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'hidden' }}>

          {/* ENCABEZADO Y KPIs */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '15px' }}>
                <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: '#0f172a' }}>Gestión de Compras</Title>
                <div style={{ background: '#f8fafc', padding: isMobile ? '12px' : '10px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', width: isMobile ? '100%' : 'auto' }}>
                  <div style={{ display: 'flex', gap: isMobile ? '10px' : '25px', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap' }}>
                    <Space direction="vertical" size={0} align="center">
                      <Text type="secondary" style={{ fontSize: '10px' }}>FACTURAS</Text>
                      <Text strong style={{ fontSize: isMobile ? '14px' : '18px' }}>{statsPeriodo.cantidad}</Text>
                    </Space>
                    <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                    <Space direction="vertical" size={0} align="center">
                      <Text type="secondary" style={{ fontSize: '10px' }}>INVERSIÓN TOTAL</Text>
                      <Text strong style={{ fontSize: isMobile ? '14px' : '18px', color: '#10b981' }}>${statsPeriodo.totalInvertido.toLocaleString('es-CO')}</Text>
                    </Space>
                    <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                    <Space direction="vertical" size={0} align="center">
                      <Text type="secondary" style={{ fontSize: '10px' }}>POR PAGAR (DEUDA)</Text>
                      <Text strong style={{ fontSize: isMobile ? '14px' : '18px', color: '#ef4444' }}>
                        {statsPeriodo.totalPorPagar > 0 && <WarningOutlined style={{ marginRight: 4 }} />}
                        ${statsPeriodo.totalPorPagar.toLocaleString('es-CO')}
                      </Text>
                    </Space>
                  </div>
                </div>
              </div>
            </div>

            {/* BARRA DE BÚSQUEDA Y ACCIONES PRINCIPALES */}
            <Row gutter={[12, 12]} style={{ marginBottom: '16px' }} align="bottom">
              <Col xs={24} md={10} lg={12}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}>Buscar (Proveedor o Factura)</Text>
                  <Input placeholder="Ej: Distribuidora XYZ..." prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} style={{ height: '40px', borderRadius: '8px' }} value={busqueda} allowClear onChange={(e) => setBusqueda(e.target.value)} />
                </Space>
              </Col>

              <Col xs={12} md={6} lg={6}>
                <Button
                  type="default"
                  block
                  icon={<FilterOutlined />}
                  style={{ height: '40px', borderRadius: '8px', borderColor: mostrarFiltros ? '#22BABB' : '#d9d9d9', color: mostrarFiltros ? '#22BABB' : 'rgba(0, 0, 0, 0.88)' }}
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                >
                  Filtros Avanzados
                </Button>
              </Col>

              <Col xs={12} md={8} lg={6}>
                <Button
                  type="primary" block icon={<PlusOutlined />}
                  style={{ height: '40px', borderRadius: '8px', background: '#22BABB', fontWeight: 'bold' }}
                  disabled={!hasPermission('compras:crear')} title={!hasPermission('compras:crear') ? 'Sin permisos' : 'Registrar Compra'}
                  onClick={() => { setEditingCompra(null); setIsModalOpen(true); form.resetFields(); setItemsCompra([]); }}
                >
                  {isMobile ? 'NUEVO' : 'NUEVA COMPRA'}
                </Button>
              </Col>
            </Row>

            {/* FILTROS AVANZADOS (COLAPSABLES) */}
            {mostrarFiltros && (
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', marginBottom: '16px', border: '1px solid #e2e8f0', animation: 'fadeIn 0.3s' }}>
                <Row gutter={[12, 12]} align="bottom">
                  <Col xs={24} md={8}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}>Filtros rápidos</Text>
                      <Radio.Group defaultValue="mes" onChange={aplicarFiltroRapido} style={{ width: '100%', display: 'flex' }}>
                        <Radio.Button value="hoy" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '38px' }}>Hoy</Radio.Button>
                        <Radio.Button value="semana" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '38px' }}>Semana</Radio.Button>
                        <Radio.Button value="mes" style={{ flex: 1, textAlign: 'center', height: '40px', lineHeight: '38px' }}>Mes</Radio.Button>
                      </Radio.Group>
                    </Space>
                  </Col>

                  <Col xs={12} md={4}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}><span style={{ color: '#000', fontWeight: '700' }}>Desde </span>(día, mes, año)</Text>
                      <DatePicker value={fechaDesde} onChange={(val) => setFechaDesde(val || dayjs())} disabledDate={d => d && d > fechaHasta} format="DD/MM/YYYY" style={{ width: '100%', height: '40px', borderRadius: '8px' }} allowClear={false} />
                    </Space>
                  </Col>

                  <Col xs={12} md={4}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}><span style={{ color: '#000', fontWeight: '700' }}>Hasta </span>(día, mes, año)</Text>
                      <DatePicker value={fechaHasta} onChange={(val) => setFechaHasta(val || dayjs())} disabledDate={d => d && d < fechaDesde.startOf('day')} format="DD/MM/YYYY" style={{ width: '100%', height: '40px', borderRadius: '8px' }} allowClear={false} />
                    </Space>
                  </Col>

                  <Col xs={12} md={4}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}>Estado</Text>
                      <Select value={estadoFiltro} onChange={setEstadoFiltro} style={{ width: '100%', height: '40px' }} options={[{ value: 'todos', label: 'Todos' }, { value: 'Pagado', label: 'Pagados' }, { value: 'Por Pagar', label: 'Por Pagar' }]} />
                    </Space>
                  </Col>

                  <Col xs={12} md={4}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '11px', fontWeight: '500' }}>Proveedor</Text>
                      <Select value={proveedorFiltro} onChange={setProveedorFiltro} style={{ width: '100%', height: '40px' }} showSearch optionFilterProp="label" options={[{ value: 'todos', label: 'Todos' }, ...proveedoresDB.map(p => ({ label: p.nombre, value: p.id }))]} />
                    </Space>
                  </Col>
                </Row>
              </div>
            )}
          </div>

          {/* --- CONTENEDOR DE LA TABLA / LISTA --- */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {isMobile ? (
              <div style={{ height: '100%', overflowY: 'auto', paddingBottom: '20px' }}>
                <List
                  dataSource={comprasFiltradas} loading={cargandoCompras}
                  renderItem={c => (
                    <Card
                      style={{ marginBottom: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
                      styles={{ body: { padding: 0 } }} // Quitamos el padding por defecto para controlar el diseño exacto
                    >
                      {/* ZONA SUPERIOR: Info Principal (Blanca) */}
                      <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

                        {/* Izquierda: Proveedor y Factura */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Text strong style={{ fontSize: '13px', color: '#0f172a', lineHeight: 1.2 }}>
                            {c.proveedor_nombre?.toUpperCase()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Fac: <Text strong style={{ color: '#64748b' }}>{c.numero_factura || 'S/N'}</Text>
                            <span style={{ margin: '0 6px', color: '#cbd5e1' }}>|</span>
                            <span style={{ color: '#0ea5e9', fontWeight: '600' }}><ShopOutlined /> {c.bodega_nombre || 'Principal'}</span>
                          </Text>
                        </div>

                        {/* Derecha: Total y Fecha */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          <Text strong style={{ fontSize: '14px', color: '#10b981', lineHeight: 1.2 }}>
                            ${Number(c.total || 0).toLocaleString('es-CO')}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            <CalendarOutlined style={{ marginRight: '4px' }} />{dayjs(c.fecha).format('DD/MM/YY')}
                          </Text>
                        </div>
                      </div>

                      {/* ZONA INFERIOR: Detalles y Acciones (Gris claro) */}
                      <div style={{ background: '#f8fafc', padding: '6px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                        {/* Izquierda: Método y Estado */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {renderMetodoPago(c.metodo_pago)}
                          <Tag color={c.estado_pago === 'Por Pagar' ? 'error' : 'success'} style={{ margin: 0, borderRadius: '4px', fontSize: '10px', border: 'none' }}>
                            {c.estado_pago?.toUpperCase()}
                          </Tag>
                        </div>

                        {/* Derecha: Botones de Acción Agrupados */}
                        <Space size={0}>
                          <Button type="text" size="small" icon={<FileTextOutlined />} style={{ color: '#64748b' }} onClick={() => { setCompraSeleccionada(c); setIsDetalleOpen(true); }} />

                          {c.metodo_pago === 'Efectivo Caja' && c.sesion_caja_id !== cajaActiva?.id ? (
                            <>
                              <Button type="text" size="small" disabled icon={<EditOutlined />} />
                              <Button type="text" size="small" disabled danger icon={<DeleteOutlined />} />
                            </>
                          ) : (
                            <>
                              <Button type="text" size="small" icon={<EditOutlined />} style={{ color: '#22BABB' }} onClick={() => handleEditCompra(c)} />
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={!hasPermission('compras:eliminar')} onClick={() => handleEliminarCompra(c)} />
                            </>
                          )}
                        </Space>
                      </div>
                    </Card>
                  )}
                  locale={{ emptyText: 'No hay compras registradas' }}
                />
              </div>
            ) : (
              <Table
                className="tabla-compras-compacta"
                columns={columnasHistorial}
                dataSource={comprasFiltradas}
                rowKey="id"
                loading={cargandoCompras}
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                  showTotal: (total) => `Total: ${total} registros`
                }}
                scroll={{ y: 'calc(100vh - 360px)' }}
                style={{ border: '1px solid #f0f0f0', borderRadius: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}
              />
            )}
          </div>
        </div>

        <ModalNuevaCompra
          open={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingCompra(null); }}
          cajaActiva={cajaActiva}
          proveedoresDB={proveedoresDB}
          productosDB={productosDB}
          compraAEditar={editingCompra}
        />

        <Modal
          title={null}
          open={isDetalleOpen}
          onCancel={() => setIsDetalleOpen(false)}
          width={900}
          centered
          closeIcon={false}
          styles={{ body: { padding: 0 } }}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 12px 12px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>Documento verificado e integrado al inventario</Text>
              <Button type="primary" size="large" onClick={() => setIsDetalleOpen(false)} style={{ background: '#0f172a', borderRadius: '8px', fontWeight: 'bold', padding: '0 25px' }}>Cerrar Documento</Button>
            </div>
          }
        >
          {compraSeleccionada && (() => {
            const prov = proveedoresDB.find(p => p.id === compraSeleccionada.proveedor_id);

            // Calculamos subtotal base e iva general aproximado de los items si no viene desglosado
            const totalFactura = Number(compraSeleccionada.total || 0);

            return (
              <div style={{ padding: '32px', background: '#fff', borderRadius: '12px' }}>

                {/* CABECERA ESTILO FACTURA TÉRMICA / DIGITAL */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #e2e8f0', paddingBottom: '20px', marginBottom: '24px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <ShopOutlined style={{ fontSize: '20px', color: '#22BABB' }} />
                      <Title level={4} style={{ margin: 0, color: '#0f172a', letterSpacing: '0.5px' }}>FACTURA DE COMPRA</Title>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Comprobante oficial de entrada al sistema</Text>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#22BABB', fontFamily: 'monospace', display: 'block' }}>
                      {compraSeleccionada.numero_factura || 'S/N'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{dayjs(compraSeleccionada.fecha).format('DD/MM/YYYY HH:mm')}</Text>
                    <div style={{ marginTop: '6px' }}>
                      <Tag color={compraSeleccionada.estado_pago === 'Por Pagar' ? 'error' : 'success'} style={{ fontWeight: 'bold', borderRadius: '4px' }}>
                        {compraSeleccionada.estado_pago?.toUpperCase()}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* BLOQUE DE INFORMACIÓN DEL PROVEEDOR Y PAGO */}
                <Row gutter={16} style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>

                  <Col xs={24} md={8}>
                    <Text style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>PROVEEDOR</Text>
                    <Text strong style={{ fontSize: '15px', color: '#0f172a' }}>{compraSeleccionada.proveedor_nombre?.toUpperCase()}</Text>
                    <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
                      NIT/CC: {prov?.nit || prov?.documento || 'No registrado'} | Tel: {prov?.telefono || 'No registrado'}
                    </Text>
                  </Col>

                  <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'center', marginTop: isMobile ? '10px' : '0', borderLeft: isMobile ? 'none' : '1px solid #e2e8f0', borderRight: isMobile ? 'none' : '1px solid #e2e8f0' }}>
                    <Text style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>BODEGA DESTINO</Text>
                    <Text strong style={{ fontSize: '15px', color: '#0f172a' }}>{compraSeleccionada.bodega_nombre || 'Bodega Principal'}</Text>
                  </Col>

                  <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? '10px' : '0' }}>
                    <Text style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>MÉTODO DE PAGO</Text>
                    <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>{compraSeleccionada.metodo_pago}</Text>
                  </Col>

                </Row>

                {/* TABLA DE ITEMS */}
                <Text strong style={{ display: 'block', marginBottom: '10px', fontSize: '13px', color: '#475569' }}>
                  DETALLE DE PRODUCTOS ADQUIRIDOS:
                </Text>
                <Table
                  dataSource={compraSeleccionada.items || []}
                  pagination={false}
                  size="middle"
                  scroll={{ y: 350 }}
                  rowKey={(r, i) => r.producto_id || i}
                  style={{ marginBottom: '24px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  columns={[
                    { title: 'Producto', dataIndex: 'nombre', render: (t) => <Text strong style={{ color: '#1e293b' }}>{t}</Text> },
                    { title: 'Cant.', dataIndex: 'cantidad', align: 'center', width: 80, render: (v) => <Tag color="default">{v}</Tag> },
                    { title: 'Costo Unit.', dataIndex: 'costo_unitario', align: 'right', width: 130, render: (t) => `$${Number(t || 0).toLocaleString('es-CO')}` },
                    { title: 'Subtotal', dataIndex: 'subtotal', align: 'right', width: 140, render: (t) => <Text strong style={{ color: '#0f172a' }}>${Number(t || 0).toLocaleString('es-CO')}</Text> }
                  ]}
                />

                {/* RESUMEN FINANCIERO / TOTALES */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: '280px', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <Row justify="space-between" style={{ marginBottom: '6px' }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>Subtotal Base:</Text>
                      <Text style={{ fontSize: '13px' }}>${Number(totalFactura).toLocaleString('es-CO')}</Text>
                    </Row>
                    <Row justify="space-between" style={{ marginBottom: '10px' }}>
                      <Text type="secondary" style={{ fontSize: '13px' }}>Impuestos (IVA):</Text>
                      <Text style={{ fontSize: '13px' }}>$0,00</Text>
                    </Row>
                    <Row justify="space-between" style={{ borderTop: '1px solid #cbd5e1', paddingTop: '10px', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>Total a Pagar:</Text>
                      <Title level={4} style={{ margin: 0, color: '#10b981' }}>
                        ${Number(totalFactura).toLocaleString('es-CO')}
                      </Title>
                    </Row>
                  </div>
                </div>

              </div>
            );
          })()}
        </Modal>

      </LayoutHome>
    </ConfigProvider>
  );
};

export default Compras;